import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { FaCar, FaClipboardList, FaEnvelope, FaPhoneSquare, FaTimesCircle } from 'react-icons/fa';

import { getCustomer, updateCustomer } from 'actions/customerActions';
import Customer from 'models/CustomerModel';
import CustomerRepairOrders from './tabs/CustomerRepairOrders';
import CustomerVehicles from './tabs/CustomerVehicles';
import CustomerStats from './CustomerStats';
import CustomerDetailsForm from './CustomerDetailsForm';

import axios from 'helpers/axios';
import history from 'helpers/history';
import config from 'helpers/config';
import VehicleModal from 'components/modals/vehicle/VehicleModal';
import CustomerOrVehiclePayments from './tabs/CustomerOrVehiclePayments';
import LabelPicker from 'components/common/LabelPicker';
import Label from 'models/LabelModel';
import { Car, FileText } from 'lucide-react';
import StartingRONumberModal from 'components/pages/jobCentre/jobList/StartingRONumberModal';
import * as RepairOrderHelper from 'helpers/repairOrderHelper';

interface Props {
    dispatch: Dispatch;
    customer: Customer;
    customerLoading: boolean;
}

interface State {
    activeTab: number;
    isNewRepairOrderLoading: boolean;
    isCreateVehicleModalOpen: boolean;
    isStartingNumberModalOpen: boolean;
}

class CustomerDetailsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            activeTab: 0,
            isNewRepairOrderLoading: false,
            isCreateVehicleModalOpen: false,
            isStartingNumberModalOpen: false,
        };

        this.getCustomerDetails = this.getCustomerDetails.bind(this);
        this.handleNewVehicleModalClosed = this.handleNewVehicleModalClosed.bind(this);
        this.getCustomerDetails();
    }

    getCustomerDetails() {
        // @ts-ignore
        const { customerId } = this.props.match.params;
        const { dispatch } = this.props;
        dispatch<any>(getCustomer(customerId, true));
    }

    getTabs() {
        return [
            {
                title: 'Repair Orders',
                component: (
                    <CustomerRepairOrders
                        repairOrders={this.props.customer?.repairOrders!}
                        vehicles={this.props.customer?.vehicles!}
                    />
                ),
            },
            {
                title: 'Vehicles',
                component: <CustomerVehicles vehicles={this.props.customer?.vehicles!} />,
            },
            {
                title: 'Payments',
                component: (
                    <CustomerOrVehiclePayments repairOrders={this.props.customer?.repairOrders!} isForCustomer={true} />
                ),
            },
            {
                title: 'Invoices',
                component: (
                    <CustomerRepairOrders
                        repairOrders={this.props.customer?.repairOrders!}
                        vehicles={this.props.customer?.vehicles!}
                    />
                ),
            },
        ];
    }

    async goToRepairOrder() {
        this.setState({ isNewRepairOrderLoading: true });

        await RepairOrderHelper.createAndNavigateToRepairOrder(
            { customerId: this.props.customer.id },
            () => {
                this.setState({ isStartingNumberModalOpen: true });
            }
        );

        this.setState({ isNewRepairOrderLoading: false });
    }

    handleStartingNumberConfirm = async (startingNumber: number) => {
        try {
            await RepairOrderHelper.updateStartingRONumber(startingNumber);
            this.setState({ isStartingNumberModalOpen: false, isNewRepairOrderLoading: true });

            const roId = await RepairOrderHelper.createRepairOrder({ customerId: this.props.customer.id });
            if (roId) {
                history.push(`/repairOrder/${roId}`);
            }
        } catch (error) {
            console.error('Error creating repair order:', error);
        } finally {
            this.setState({ isNewRepairOrderLoading: false });
        }
    };

    handleNewVehicleModalClosed(vehicleCreated?: boolean) {
        if (vehicleCreated) {
            this.getCustomerDetails();
        }
        this.setState({
            isCreateVehicleModalOpen: false,
        });
    }

    removeLabel(labelIndex: number) {
        const { labels } = this.props.customer;
        if (labels?.length) {
            labels.splice(labelIndex, 1);
            const { customer } = this.props;
            customer.labels = labels;
            const { dispatch } = this.props;
            dispatch<any>(updateCustomer(customer!, true));
        }
    }

    render() {
        const { customerLoading, customer, dispatch } = this.props;
        const { activeTab, isNewRepairOrderLoading, isCreateVehicleModalOpen, isStartingNumberModalOpen } = this.state;
        const tabs = this.getTabs();
        return (
            <div className="bg-gray-100 pb-96 relative">
                <StartingRONumberModal
                    isOpen={isStartingNumberModalOpen}
                    onClose={() => this.setState({ isStartingNumberModalOpen: false })}
                    onConfirm={this.handleStartingNumberConfirm}
                />
                {customerLoading && (
                    <div className="page-loading">
                        <div className="spinner"></div>
                    </div>
                )}
                {/* {!!customerLoading && <b>Loading...</b>} */}
                {isCreateVehicleModalOpen && (
                    <VehicleModal onClose={this.handleNewVehicleModalClosed} customer={this.props.customer} />
                )}
                <div className="flex w-full bg-blue-500 p-8 pb-2">
                    <div className="font-semibold ">
                        <h3 className="text-blue-100">Customer</h3>
                        <h2 className="text-blue-50 text-2xl font-semibold">
                            {customer?.firstName} {customer?.lastName}
                        </h2>
                    </div>

                    <div className="flex gap-3 ml-auto">
                        <button
                            disabled={isNewRepairOrderLoading}
                            onClick={() => this.goToRepairOrder()}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center transition-colors shadow-sm"
                        >
                            <FileText size={16} className="mr-2" />
                            New Invoice / Repair Order
                        </button>
                        <button
                            disabled={isNewRepairOrderLoading}
                            onClick={() => this.setState({ isCreateVehicleModalOpen: true })}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors shadow-sm"
                        >
                            <Car size={16} className="mr-2" />
                            New Vehicle
                        </button>
                    </div>
                </div>
                <div className="w-full text-white from-blue-700 to-blue-500 px-8 py-2 bg-gradient-to-r">
                    <div className="pb-2">
                        {!!customer?.primaryEmail && (
                            <span>
                                <FaEnvelope className="inline align-baseline mr-2" />
                                <a className="mr-2" href={`mailto:${customer.primaryEmail}`}>
                                    {customer.primaryEmail}
                                </a>
                            </span>
                        )}
                    </div>
                    <div>
                        {customer?.phoneNumbers?.map((number) => (
                            <span key={number.phoneNumber}>
                                <FaPhoneSquare className="inline align-baseline mr-2" />
                                <a className="mr-2" href={`tel:${number.phoneNumber}`}>
                                    {number.phoneNumber}
                                </a>
                            </span>
                        ))}
                    </div>
                    <div className="flex">
                        <div className=" mr-1.5">Labels: </div>
                        {customer?.labels?.map((label, indx) => (
                            <span
                                key={indx}
                                style={{ backgroundColor: label.color }}
                                className="mr-2 px-1 py-1 rounded-lg hover:opacity-80 text-xs"
                            >
                                <span className="font-semibold text-white inline-flex whitespace-nowrap">
                                    <FaTimesCircle
                                        onClick={() => this.removeLabel(indx)}
                                        className="inline mr-1 clickable align-baseline text-xs"
                                    />
                                    {label.title}
                                </span>
                            </span>
                        ))}
                        <LabelPicker
                            labelType={'customer'}
                            labelsSelected={(labels: Label[]) => {
                                const { customer } = this.props;
                                // @ts-ignore
                                customer!.labels = labels;
                                const { dispatch } = this.props;
                                dispatch<any>(updateCustomer(customer!, true));
                            }}
                            labelIds={customer?.labels?.map((label) => label.id)}
                        />
                    </div>
                </div>
                <div className="px-6 flex flex-col">
                    <div className="flex flex-col md:flex-row w-full gap-4 mt-4">
                        <div className="w-full ">{!!customer && <CustomerStats customer={customer} />}</div>
                        <div
                            className=" w-full relative flex flex-col h-fit
                    bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm
                    "
                        >
                            {!!customerLoading && (
                                <div className="absolute left-1/2 top-1/3">
                                    <h2 className="font-bold">Loading...</h2>
                                </div>
                            )}
                            {!customerLoading && !!customer && (
                                <CustomerDetailsForm
                                    customer={customer}
                                    showEditButton={true}
                                    onEditModeChange={(isEditing) => {
                                        // Refresh data when edit mode is turned off (saving changes)
                                        if (!isEditing) {
                                            this.getCustomerDetails();
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    <div className="mt-8">
                        {tabs.map((tab, index) => {
                            return (
                                <div
                                    className={`inline-flex px-5 border-b-4 ${
                                        activeTab === index
                                            ? ' border-blue-500 '
                                            : '  border-gray-500 hover:text-blue-400'
                                    }`}
                                    key={index}
                                >
                                    <span
                                        className={`clickable text-lg ${
                                            activeTab === index
                                                ? 'font-bold text-blue-500 border-blue-500 '
                                                : ' font-medium text-gray-400 border-gray-500 hover:text-blue-400'
                                        }`}
                                        onClick={() => this.setState({ activeTab: index })}
                                    >
                                        {tab.title}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    {tabs[activeTab].component}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    customer: state.customer.customer,
    customerLoading: state.customer.customerLoading,
});

export default connect(mapStateToProps)(CustomerDetailsPage);
